import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import PoweredBy from "@/components/PoweredBy";
import { Waves, LogIn, ArrowRight, Eye, EyeOff } from "lucide-react";

// Time-based restrictions have been replaced by the global registration toggle

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);

  React.useEffect(() => {
    api.get("/meta/events").then(({ data }) => setRegistrationOpen(data.registration_open)).catch(() => {});
  }, []);


  const onForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If the account exists, a reset link has been emailed.");
      setShowForgot(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "logistics_team") navigate("/team/logistics", { replace: true });
      else if (user.role === "accommodation_team") navigate("/team/accommodation", { replace: true });
      else if (user.role === "security_team") navigate("/team/security", { replace: true });
      else if (user.role === "delegate") navigate("/team/delegate", { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-16 px-4 sm:px-6 grid md:grid-cols-2 max-w-6xl mx-auto gap-12 items-center">
        <div className="hidden md:block">
          <Waves className="w-12 h-12 text-[#00F0FF] mb-4" strokeWidth={1.5} />
          <div className="eyebrow mb-3">Athlete Portal</div>
          <h1 className="font-display font-black text-5xl leading-tight">Return to your <span className="text-[#00F0FF]">dashboard</span>.</h1>
          <p className="mt-5 text-[#94A3B8] text-lg">Manage your registration, payments, accommodation and travel logistics — all in one place.</p>
        </div>
        <div className="card-dark p-8 md:p-10">
          <div className="flex items-center gap-2 mb-6">
            <LogIn className="w-5 h-5 text-[#00F0FF]" />
            <h2 className="font-display font-bold text-2xl">Athlete Login</h2>
          </div>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Email</label>
              <input id="login-email" data-testid="login-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" placeholder="you@example.com" />
            </div>
            <div>
              <label htmlFor="login-password" className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Password</label>
              <div className="relative">
                <input id="login-password" data-testid="login-password" type={showPwd ? "text" : "password"} required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark pr-12" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label={showPwd ? "Hide password" : "Show password"} className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-[#94A3B8] hover:text-[#00F0FF] touch-manipulation" data-testid="toggle-password">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2 min-h-[44px] touch-manipulation">
              {loading ? "Signing in..." : "Sign In"} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-right">
              <button type="button" onClick={() => setShowForgot(!showForgot)} className="text-xs text-[#00F0FF] hover:underline" data-testid="forgot-link">
                Forgot password?
              </button>
            </div>
            {showForgot && (
              <div className="bg-[#0B1429] border border-[#2A3A5F] rounded-lg p-4 mt-3" data-testid="forgot-form">
                <div className="text-xs text-[#94A3B8] mb-3">Enter your registered email — we'll send a reset link.</div>
                <div className="flex gap-2">
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" className="input-dark flex-1" data-testid="forgot-email" />
                  <button onClick={onForgot} type="button" className="btn-primary !py-2 !px-4 text-xs" data-testid="forgot-submit">Send</button>
                </div>
              </div>
            )}
          </form>
          <div className="mt-6 text-sm text-[#94A3B8] text-center">
            {registrationOpen ? (
              <>New athlete? <Link to="/register" className="text-[#00F0FF] hover:underline" data-testid="link-to-register">Create account</Link></>
            ) : (
              <span data-testid="link-to-register-closed">Account registration is now closed.</span>
            )}
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
}
